/**
 * Logistics — fuel + ammo consumption, attrition, building/truck resupply.
 *
 * `fuel` is what powers movement; air/water also burn it while idle. Land
 * units at `fuel === 0` are immobilised but stay alive. Air/water units at
 * `fuel === 0` are destroyed (plane crashes, ship sinks).
 *
 * `ammo` is what powers attacks. A unit at `ammo === 0` cannot initiate an
 * attack. Counter-attacks are free (intentional — encourages defence).
 *
 * Pure functions on TurnWorld, no Phaser dependencies. File name kept as
 * `supply.ts` because the broader resupply concept (trucks, building yards)
 * still applies; only the runtime field split changed.
 */
import type { TurnWorld, Unit, PlayerId, Domain } from './types';
import { UNIT_DEFS, resolveBaseType } from './units';
import { BUILDING_DEFS } from './buildings';

// ---------------------------------------------------------------------------
// Effective stats — penalty when ammo runs out
// ---------------------------------------------------------------------------

/** Effective attack — halved (min 1) when a unit has no ammo. Most callers
 *  also short-circuit via `canAttack`, but the penalty stays as a safety net. */
export function getEffectiveAttack(unit: Unit): number {
  const base = UNIT_DEFS[unit.type].attack;
  if (base === 0) return 0;
  if (unit.ammo > 0) return base;
  return Math.max(1, Math.floor(base * 0.5));
}

/** Effective movement at turn start. Foot units (maxFuel=0) always get
 *  full range. Land units with `fuel === 0` are immobilised (return 0).
 *  Air/water with `fuel === 0` get half-range as a last-ditch escape — they
 *  die at end of turn anyway (see `applyAttrition`). */
export function getEffectiveMovement(unit: Unit): number {
  const def = UNIT_DEFS[unit.type];
  const base = def.movementRange;
  if (def.maxFuel === 0) return base;
  if (unit.fuel > 0) return base;
  if (def.domain === 'land') return 0;
  return Math.max(1, Math.floor(base * 0.5));
}

// ---------------------------------------------------------------------------
// Idle fuel consumption (end-of-turn step 1)
// ---------------------------------------------------------------------------

/** Burn fuel for air/water units that exist this turn. Land units don't
 *  consume idle fuel (an idle tank in cover does not run its engine). */
export function consumeIdleFuel(world: TurnWorld, playerId: PlayerId): void {
  for (const u of world.units.values()) {
    if (u.owner !== playerId) continue;
    const def = UNIT_DEFS[u.type];
    if (def.domain === 'land') continue;
    if (def.fuelCostIdle <= 0) continue;
    u.fuel = Math.max(0, u.fuel - def.fuelCostIdle);
  }
}

// Back-compat alias — callers still use this name.
export const consumeIdleSupply = consumeIdleFuel;

// ---------------------------------------------------------------------------
// Attrition (end-of-turn step 2)
// ---------------------------------------------------------------------------

export interface AttritionResult {
  unitId: number;
  hpBefore: number;
  hpAfter: number;
  died: boolean;
}

/**
 * Apply attrition at end of turn:
 *  - Air/water with `fuel === 0` → destroyed (crash / sink). Returned with
 *    `died: true`. HP fields are filled in but irrelevant.
 *  - Land with `fuel === 0` → no HP damage, no removal (immobilised only).
 */
export function applyAttrition(world: TurnWorld, playerId: PlayerId): AttritionResult[] {
  const results: AttritionResult[] = [];
  const toRemove: number[] = [];

  for (const u of world.units.values()) {
    if (u.owner !== playerId) continue;
    const def = UNIT_DEFS[u.type];
    if (def.domain === 'land') continue;     // land just sits there
    if (def.maxFuel === 0) continue;         // shouldn't happen for air/water but defensive
    if (u.fuel > 0) continue;

    const hpBefore = u.hp;
    u.hp = 0;
    results.push({ unitId: u.id, hpBefore, hpAfter: 0, died: true });
    toRemove.push(u.id);
  }

  for (const id of toRemove) world.units.delete(id);

  return results;
}

// ---------------------------------------------------------------------------
// Building resupply (end-of-turn step 3)
// ---------------------------------------------------------------------------

function chebyshev(a: { col: number; row: number }, b: { col: number; row: number }): number {
  return Math.max(Math.abs(a.col - b.col), Math.abs(a.row - b.row));
}

export interface ResupplyResult {
  unitId: number;
  /** Tracks how much was restored. Filled-by-max semantics: before vs after. */
  fuelBefore: number;
  fuelAfter: number;
  ammoBefore: number;
  ammoAfter: number;
}

export interface BuildingResupplyResult {
  buildingId: number;
  col: number;
  row: number;
  resupplies: ResupplyResult[];
}

/** Refill both fuel and ammo on the target to their respective maxima.
 *  Returns whether anything changed (so callers can skip pointless animation). */
function refillUnit(u: Unit): ResupplyResult | null {
  const def = UNIT_DEFS[u.type];
  const fuelBefore = u.fuel;
  const ammoBefore = u.ammo;
  if (u.fuel >= def.maxFuel && u.ammo >= def.maxAmmo) return null;
  u.fuel = def.maxFuel;
  u.ammo = def.maxAmmo;
  return { unitId: u.id, fuelBefore, fuelAfter: u.fuel, ammoBefore, ammoAfter: u.ammo };
}

/**
 * Resupply units adjacent to production buildings at end of turn.
 * Production buildings: hq, barracks, vehicle-factory, motor-pool, airport, port.
 * HQ resupplies all types; others resupply their recruitment roster.
 */
export function executeBuildingResupply(world: TurnWorld, playerId: PlayerId): BuildingResupplyResult[] {
  const results: BuildingResupplyResult[] = [];

  for (const building of world.buildings.values()) {
    if (building.owner !== playerId) continue;

    const bDef = BUILDING_DEFS[building.type];
    if (!bDef.repairsAll && !bDef.recruits) continue;

    const resupplies: ResupplyResult[] = [];
    for (const u of world.units.values()) {
      if (u.owner !== playerId) continue;
      const def = UNIT_DEFS[u.type];
      // Skip units that consume neither resource (extremely rare; supply-truck
      // has fuel but the early-out below handles "already full").
      if (def.maxFuel === 0 && def.maxAmmo === 0) continue;
      if (chebyshev(building, u) > 1) continue;
      // Faction-unique units (iii-rzesza-stuka, usa-lcvp-higgins, …) inherit
      // their base type's recruitment building — resolve to the base so
      // planes get refilled at the airport, ships at the port, etc.
      if (!bDef.repairsAll) {
        const baseType = resolveBaseType(u.type);
        if (!bDef.recruits!.includes(u.type) && !bDef.recruits!.includes(baseType)) continue;
      }

      const r = refillUnit(u);
      if (r) resupplies.push(r);
    }

    if (resupplies.length > 0) {
      results.push({ buildingId: building.id, col: building.col, row: building.row, resupplies });
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// Mobile resupplier order (active, like medic heal)
// ---------------------------------------------------------------------------

/** The movement domain a unit can actively resupply, or null if it's not a
 *  resupplier. Resolved through the base type so faction-unique variants inherit
 *  the capability (same pattern as `executeBuildingResupply`). */
export function resolveResupplyDomain(unitType: Unit['type']): Domain | null {
  return UNIT_DEFS[unitType].resuppliesDomain
    ?? UNIT_DEFS[resolveBaseType(unitType)].resuppliesDomain
    ?? null;
}

/** Adjacent friendly units of the resupplier's served domain that aren't already
 *  topped up. Empty when the unit isn't a resupplier. */
export function computeResupplyTargets(world: TurnWorld, resupplierId: number): Unit[] {
  const src = world.units.get(resupplierId);
  if (!src) return [];
  const domain = resolveResupplyDomain(src.type);
  if (domain === null) return [];

  const targets: Unit[] = [];
  for (const u of world.units.values()) {
    if (u.id === resupplierId) continue;
    if (u.owner !== src.owner) continue;
    const def = UNIT_DEFS[u.type];
    if (def.domain !== domain) continue;       // domain-scoped: trucks→land, carriers→air, …
    if (def.maxFuel === 0 && def.maxAmmo === 0) continue;
    if (u.fuel >= def.maxFuel && u.ammo >= def.maxAmmo) continue;
    if (chebyshev(src, u) > 1) continue;
    targets.push(u);
  }
  return targets;
}

/** Resupplier refills every eligible adjacent friendly to max fuel + max ammo,
 *  then ends its turn. Unlimited supply to give (no internal pool). */
export function executeResupply(world: TurnWorld, resupplierId: number): ResupplyResult[] {
  const src = world.units.get(resupplierId);
  if (!src) return [];

  const targets = computeResupplyTargets(world, resupplierId);
  const results: ResupplyResult[] = [];

  for (const target of targets) {
    const r = refillUnit(target);
    if (r) results.push(r);
  }

  src.acted = true;
  src.movePoints = 0;
  return results;
}

// ---------------------------------------------------------------------------
// Movement fuel cost
// ---------------------------------------------------------------------------

/** Deduct fuel for `tileCount` tiles of movement. Foot units (maxFuel=0)
 *  are no-ops. */
export function consumeFuelForMove(unit: Unit, tileCount: number): void {
  const def = UNIT_DEFS[unit.type];
  if (def.maxFuel === 0) return;
  const cost = def.fuelCostMove * tileCount;
  if (cost > 0) unit.fuel = Math.max(0, unit.fuel - cost);
}

// Back-compat alias for callers that still import the old name.
export const consumeMoveSupply = consumeFuelForMove;

// ---------------------------------------------------------------------------
// Attack ammo cost
// ---------------------------------------------------------------------------

/** Deduct ammo for an attack. Counter-attacks should NOT call this. */
export function consumeAmmoForAttack(unit: Unit): void {
  const def = UNIT_DEFS[unit.type];
  if (def.maxAmmo === 0) return;
  if (def.ammoCostAttack <= 0) return;
  unit.ammo = Math.max(0, unit.ammo - def.ammoCostAttack);
}

// Back-compat alias.
export const consumeAttackSupply = consumeAmmoForAttack;
